import { type AddOnsErrors } from '@components/add-ons-section';
import { type AdvancedSettingsErrors, ENTRANCE_TIME_SLOT_MODE_LIMITED } from '@components/advanced-settings-section';
import {
  type BasicInfoErrors,
  type BasicInfoValue,
  SALES_END_TYPE_CUSTOM_DATE,
  SALES_START_TYPE_CUSTOM_DATE,
} from '@components/basic-info-section';
import { CHANNEL_QUOTA_ALL } from '@components/distribution-section';
import { type RegulationSettingsErrors, SIAE_STATUS_EXCLUDED } from '@components/regulation-settings-section';
import { type VatRateErrors } from '@components/vat-rate-section';
import { ADMISSION_TYPE_BOTTLE_SERVICE, ADMISSION_TYPE_GUEST_LIST } from '@domain/admission';
import { calculateDefaultMaxCapacity } from '@utils/calculate-dafult-max-capacity/calculate-default-max-capacity';
import { calculateMaxItemsPerTransation } from '@utils/calculate-max-items-per-transation/calculate-max-items-per-transation';
import { collectChannelIds, collectClosedChannelIds } from '@utils/channel-tree';
import { cannotBeNegative } from '@utils/validations/cannot-be-negative';
import { isDateLessThan } from '@utils/validations/is-date-less-than';
import { max } from '@utils/validations/max';
import { maxDecimals } from '@utils/validations/max-decimals';
import { min } from '@utils/validations/min';
import { type TFunction } from 'i18next';
import moment from 'moment-timezone';

import { type AdmissionFormType, type AdmissionFormValue } from './admission-form.type';
import { ADMISSION_FORM_TYPE_PASS } from './constants';

const GUEST_CAPACITY_MIN = 1;
const GUEST_CAPACITY_MAX = 50;
const SIAE_MAX_TOTAL_GUESTS = 10;
const DOWNPAYMENT_MIN = 1;
const VAT_MAX = 100;
const VAT_MAX_DECIMALS = 2;
const PRICE_DECIMALS = 2;
const WHOLE_NUMBER = 0;

export interface AdmissionFormErrors {
  basicInfo?: BasicInfoErrors;
  advanced?: AdvancedSettingsErrors;
  addOns?: AddOnsErrors;
  vatRate?: VatRateErrors;
  regulation?: RegulationSettingsErrors;
}

export interface ValidationContext {
  value: AdmissionFormValue;
  type: AdmissionFormType;
  isEventPass?: boolean;
  isEventSiae?: boolean;
  mainLanguage: string;
  eventStartingTime: number;
  eventEndingTime: number;
  timezone: string;
  t: TFunction;
}

const validatePrice = (basicInfo: BasicInfoValue, selectedAddOnIds: string[], t: TFunction): string | undefined => {
  if (!basicInfo.price) return t('errors:formFieldRequired');
  return (
    cannotBeNegative({ value: basicInfo.price, message: t('offers:admissionPriceCannotBeNegativeError') }) ??
    maxDecimals({
      value: basicInfo.price,
      decimals: PRICE_DECIMALS,
      message: t('offers:admissionPriceDecimalsError'),
    }) ??
    // No upper cap: the price ceiling is currency-dependent, so it's owned by the backend
    // (offer-service `offerPriceInvalidAmount`), which surfaces as a toast on save.
    (Number(basicInfo.price) === 0 && selectedAddOnIds.length > 0 ? t('offers:admissionPriceCannotBeZero') : undefined)
  );
};

const validateQuantity = (basicInfo: BasicInfoValue, t: TFunction): string | undefined =>
  cannotBeNegative({ value: basicInfo.quantity, message: t('offers:admissionTicketQuantityNegativeError') }) ??
  maxDecimals({
    value: basicInfo.quantity,
    decimals: WHOLE_NUMBER,
    message: t('offers:admissionTicketQuantityDecimalsError'),
  });

export const getDefaultMaxItemsPerTransaction = (type: AdmissionFormType): string =>
  calculateDefaultMaxCapacity(type === ADMISSION_FORM_TYPE_PASS ? ADMISSION_TYPE_GUEST_LIST : type);

const validateItems = (
  basicInfo: BasicInfoValue,
  type: AdmissionFormType,
  t: TFunction
): Pick<BasicInfoErrors, 'itemsPerTransactionMin' | 'itemsPerTransactionMax'> => {
  const { min: minItems, max: maxItems } = basicInfo.itemsPerTransaction;
  const quantityCap = calculateMaxItemsPerTransation(basicInfo.quantity ? Number(basicInfo.quantity) : null);
  const effectiveMax =
    maxItems === ''
      ? Math.min(Number(getDefaultMaxItemsPerTransaction(type)), Number(quantityCap)).toString()
      : maxItems;
  const errors: Pick<BasicInfoErrors, 'itemsPerTransactionMin' | 'itemsPerTransactionMax'> = {};

  const minError =
    cannotBeNegative({ value: minItems, message: t('offers:admissionMinItemsPerTransactionNegativeError') }) ??
    maxDecimals({
      value: minItems,
      decimals: WHOLE_NUMBER,
      message: t('offers:admissionMinItemsPerTransactionDecimalsError'),
    }) ??
    max({
      value: Number(minItems),
      maxValue: Number(effectiveMax),
      message: t('offers:admissionMinItemsPerTransactionMaxError', { maxValue: effectiveMax }),
    });
  if (minError) errors.itemsPerTransactionMin = minError;

  const maxError =
    cannotBeNegative({ value: maxItems, message: t('offers:admissionMaxItemsPerTransactionNegativeError') }) ??
    maxDecimals({
      value: maxItems,
      decimals: WHOLE_NUMBER,
      message: t('offers:admissionMaxItemsPerTransactionDecimalsError'),
    }) ??
    max({
      value: Number(maxItems),
      maxValue: Number(quantityCap),
      message: t('offers:admissionMaxItemsPerTransactionMaxError', { maxValue: quantityCap }),
    }) ??
    min({
      value: effectiveMax,
      minValue: Number(minItems),
      message: t('offers:admissionMaxItemsPerTransactionMinError', { minValue: minItems }),
    });
  if (maxError) errors.itemsPerTransactionMax = maxError;

  return errors;
};

const validateDdp = (basicInfo: BasicInfoValue, t: TFunction): string | undefined => {
  if (!basicInfo.ddp) return undefined;
  return (
    cannotBeNegative({ value: basicInfo.ddp, message: t('offers:admissionDDPCannotBeNegativeError') }) ??
    maxDecimals({ value: basicInfo.ddp, decimals: PRICE_DECIMALS, message: t('offers:admissionDDPDecimalsError') })
  );
};

const validateDownpayment = (basicInfo: BasicInfoValue, t: TFunction): string | undefined => {
  if (!basicInfo.downpayment) return undefined;
  return (
    cannotBeNegative({ value: basicInfo.downpayment, message: t('offers:admissionPriceCannotBeNegativeError') }) ??
    maxDecimals({
      value: basicInfo.downpayment,
      decimals: PRICE_DECIMALS,
      message: t('offers:admissionPriceDecimalsError'),
    }) ??
    min({ value: basicInfo.downpayment, minValue: DOWNPAYMENT_MIN, message: t('offers:downpaymentMinError') }) ??
    (basicInfo.price
      ? max({
          value: Number(basicInfo.downpayment),
          maxValue: Number(basicInfo.price),
          message: t('offers:downpaymentError'),
        })
      : undefined)
  );
};

const validateGuests = (basicInfo: BasicInfoValue, t: TFunction): string | undefined => {
  if (basicInfo.guestsPerAdmission === undefined) return undefined;
  if (!basicInfo.guestsPerAdmission) return t('offers:admissionGuestCapacityRequiredError');
  return (
    cannotBeNegative({
      value: basicInfo.guestsPerAdmission,
      message: t('offers:admissionGuestCapacityNegativeError'),
    }) ??
    maxDecimals({
      value: basicInfo.guestsPerAdmission,
      decimals: WHOLE_NUMBER,
      message: t('offers:admissionGuestCapacityDecimalsError'),
    }) ??
    min({
      value: basicInfo.guestsPerAdmission,
      minValue: GUEST_CAPACITY_MIN,
      message: t('offers:admissionGuestCapacityMinError'),
    }) ??
    max({
      value: Number(basicInfo.guestsPerAdmission),
      maxValue: GUEST_CAPACITY_MAX,
      message: t('offers:admissionGuestCapacityMaxError'),
    })
  );
};

const validateExtraGuests = (
  basicInfo: BasicInfoValue,
  t: TFunction
): Pick<BasicInfoErrors, 'extraGuestsMaxQty' | 'extraGuestsPricePerGuest'> => {
  const errors: Pick<BasicInfoErrors, 'extraGuestsMaxQty' | 'extraGuestsPricePerGuest'> = {};
  const { extraGuests } = basicInfo;
  if (!extraGuests?.enabled) return errors;

  const maxQtyError = extraGuests.maxQty
    ? maxDecimals({
        value: extraGuests.maxQty,
        decimals: WHOLE_NUMBER,
        message: t('offers:admissionGuestCapacityDecimalsError'),
      }) ??
      min({
        value: extraGuests.maxQty,
        minValue: GUEST_CAPACITY_MIN,
        message: t('offers:admissionGuestCapacityMinError'),
      }) ??
      max({
        value: Number(extraGuests.maxQty),
        maxValue: GUEST_CAPACITY_MAX,
        message: t('offers:admissionGuestCapacityMaxError'),
      })
    : t('errors:formFieldRequired');
  if (maxQtyError) errors.extraGuestsMaxQty = maxQtyError;

  const priceError = extraGuests.pricePerGuest
    ? cannotBeNegative({
        value: extraGuests.pricePerGuest,
        message: t('offers:admissionPriceCannotBeNegativeError'),
      }) ??
      maxDecimals({
        value: extraGuests.pricePerGuest,
        decimals: PRICE_DECIMALS,
        message: t('offers:admissionPriceDecimalsError'),
      })
    : t('errors:formFieldRequired');
  if (priceError) errors.extraGuestsPricePerGuest = priceError;

  return errors;
};

const validateSalesWindow = (
  basicInfo: BasicInfoValue,
  eventEndingTime: number,
  timezone: string,
  t: TFunction
): Pick<BasicInfoErrors, 'salesStart' | 'salesEnd'> => {
  const errors: Pick<BasicInfoErrors, 'salesStart' | 'salesEnd'> = {};
  if (!basicInfo.salesWindow) return errors;

  const { startType, startDate, startTime, endType, endDate, endTime } = basicInfo.salesWindow;
  if (startType === SALES_START_TYPE_CUSTOM_DATE && !startDate) {
    errors.salesStart = t('offers:admissionSalesStartDateErrorRequired');
  }
  if (endType === SALES_END_TYPE_CUSTOM_DATE && !endDate) {
    errors.salesEnd = t('offers:admissionSalesEndDateErrorRequired');
  }
  if (!errors.salesEnd) {
    const orderingError = isDateLessThan({
      isEqualInvalid: false,
      startDate,
      startTime,
      endDate,
      endTime,
      eventEndDate: moment.unix(eventEndingTime).tz(timezone),
      message: t('offers:salesTimeGreaterThanEndTimeError'),
      messageTimeEnd: t('errors:offersEndTimeAfterEventEndingTime'),
      timezone,
    });
    if (orderingError) errors.salesEnd = orderingError;
  }
  return errors;
};

const validatePassValidity = (
  basicInfo: BasicInfoValue,
  isEventPass: boolean,
  t: TFunction
): Pick<BasicInfoErrors, 'passValidityStart' | 'passValidityEnd'> => {
  const errors: Pick<BasicInfoErrors, 'passValidityStart' | 'passValidityEnd'> = {};
  // Event passes inherit their window from the event; company/preset passes require explicit dates.
  if (isEventPass || !basicInfo.passValidity) return errors;
  if (!basicInfo.passValidity.start) errors.passValidityStart = t('errors:formFieldRequired');
  if (!basicInfo.passValidity.end) errors.passValidityEnd = t('errors:formFieldRequired');
  return errors;
};

// A non-excluded SIAE bottle service is capped at 10 total guests (base + extra) by the BE; mirror it
// so the user gets a field error instead of a rejected save.
const validateSiaeGuestCap = (
  value: AdmissionFormValue,
  type: AdmissionFormType,
  isEventSiae: boolean,
  t: TFunction
): string | undefined => {
  if (type !== ADMISSION_TYPE_BOTTLE_SERVICE || !isEventSiae) return undefined;
  const { basicInfo, regulation } = value;
  const isExcluded = regulation.siaeStatus === SIAE_STATUS_EXCLUDED || regulation.excludeFromC1 === true;
  if (isExcluded) return undefined;

  const base = Number(basicInfo.guestsPerAdmission) || 0;
  const extra = basicInfo.extraGuests?.enabled ? Number(basicInfo.extraGuests.maxQty) || 0 : 0;
  if (base + extra <= SIAE_MAX_TOTAL_GUESTS) return undefined;
  return t('offers:admissionSiaeGuestCapError', { max: SIAE_MAX_TOTAL_GUESTS });
};

const validateBasicInfo = ({
  value,
  type,
  isEventPass = false,
  isEventSiae = false,
  eventEndingTime,
  timezone,
  t,
}: ValidationContext): BasicInfoErrors => {
  const { basicInfo, addOns } = value;
  const errors: BasicInfoErrors = {
    ...validateItems(basicInfo, type, t),
    ...validateSalesWindow(basicInfo, eventEndingTime, timezone, t),
    ...validateExtraGuests(basicInfo, t),
    ...validatePassValidity(basicInfo, isEventPass, t),
  };

  const price = validatePrice(basicInfo, addOns.selectedAddOnIds, t);
  if (price) errors.price = price;
  const quantity = validateQuantity(basicInfo, t);
  if (quantity) errors.quantity = quantity;
  const ddp = validateDdp(basicInfo, t);
  if (ddp) errors.ddp = ddp;
  const downpayment = validateDownpayment(basicInfo, t);
  if (downpayment) errors.downpayment = downpayment;
  const guests = validateGuests(basicInfo, t) ?? validateSiaeGuestCap(value, type, isEventSiae, t);
  if (guests) errors.guestsPerAdmission = guests;

  return errors;
};

const validateAdvanced = ({
  value,
  eventStartingTime,
  eventEndingTime,
  timezone,
  t,
}: ValidationContext): AdvancedSettingsErrors => {
  const { advanced } = value;
  const errors: AdvancedSettingsErrors = {};

  if (advanced.drinks?.enabled) {
    const drinksError =
      cannotBeNegative({
        value: advanced.drinks.quantity,
        message: t('offers:admissionDrinksIncludedCannotBeNegativeError'),
      }) ??
      maxDecimals({
        value: advanced.drinks.quantity,
        decimals: WHOLE_NUMBER,
        message: t('offers:admissionDrinksIncludedDecimalsError'),
      });
    if (drinksError) errors.drinksQuantity = drinksError;
  }

  if (advanced.lowStockThreshold?.enabled) {
    const thresholdError =
      cannotBeNegative({
        value: advanced.lowStockThreshold.quantity,
        message: t('offers:admissionTicketsLeftThresholdCannotBeNegativeError'),
      }) ??
      maxDecimals({
        value: advanced.lowStockThreshold.quantity,
        decimals: WHOLE_NUMBER,
        message: t('offers:admissionTicketsLeftThresholdDecimalsError'),
      });
    if (thresholdError) errors.lowStockThresholdQuantity = thresholdError;
  }

  const { mode, validFrom, validFromTime, validUntil, validUntilTime } = advanced.entranceTimeSlot;
  if (mode === ENTRANCE_TIME_SLOT_MODE_LIMITED) {
    const slotError = isDateLessThan({
      startDate: validFrom,
      startTime: validFromTime,
      endDate: validUntil,
      endTime: validUntilTime,
      eventStartDate: moment.unix(eventStartingTime).tz(timezone),
      eventEndDate: moment.unix(eventEndingTime).tz(timezone),
      message: t('offers:validFromGreaterThanUntilTimeError'),
      messageTimeBefore: t('errors:invalidTimeErrorBefore'),
      messageTimeEnd: t('errors:invalidTimeErrorEnd'),
      timezone,
    });
    if (slotError) errors.entranceTimeSlotRange = slotError;
  }

  return errors;
};

const validateAddOns = ({ value, t }: ValidationContext): AddOnsErrors => {
  const { addOns } = value;
  const errors: AddOnsErrors = {};

  if (addOns.hasMandatoryAddOns && addOns.selectedAddOnIds.length === 0) {
    errors.mandatoryNoSelection = t('offers:admissionAddOnsMandatorySubtitle');
  }

  return errors;
};

const validateRegulation = ({ value, t }: ValidationContext): RegulationSettingsErrors => {
  const { regulation, addOns } = value;
  const errors: RegulationSettingsErrors = {};
  if (regulation.allows18App && addOns.selectedAddOnIds.length > 0) {
    errors.allows18App = t('offers:admissionCartaCulturaAddOnsError');
  }
  return errors;
};

const validateVatRate = ({ value, type, t }: ValidationContext): VatRateErrors => {
  const { vatRate } = value;
  const errors: VatRateErrors = {};
  if (type === ADMISSION_TYPE_GUEST_LIST || vatRate.vatRate == null) return errors;

  const raw = vatRate.vatRate.toString();
  const vatRateError =
    cannotBeNegative({ value: raw, message: t('offers:admissionVatRateCannotBeNegativeError') }) ??
    maxDecimals({ value: raw, decimals: VAT_MAX_DECIMALS, message: t('offers:admissionVatRateDecimalsError') }) ??
    max({ value: vatRate.vatRate, maxValue: VAT_MAX, message: t('offers:admissionVatRateMaxError') });
  if (vatRateError) errors.vatRate = vatRateError;

  return errors;
};

const hasAnyError = (errors: object): boolean => Object.keys(errors).length > 0;

export const validateAdmissionForm = (ctx: ValidationContext): AdmissionFormErrors => {
  const basicInfo = validateBasicInfo(ctx);
  const advanced = validateAdvanced(ctx);
  const addOns = validateAddOns(ctx);
  const vatRate = validateVatRate(ctx);
  const regulation = validateRegulation(ctx);

  return {
    ...(hasAnyError(basicInfo) && { basicInfo }),
    ...(hasAnyError(advanced) && { advanced }),
    ...(hasAnyError(addOns) && { addOns }),
    ...(hasAnyError(vatRate) && { vatRate }),
    ...(hasAnyError(regulation) && { regulation }),
  };
};

export const isAdmissionFormValid = (errors: AdmissionFormErrors): boolean => Object.keys(errors).length === 0;

/**
 * Channel-limit validation that lives outside the section-error tree (mirrors the legacy
 * `salesQuota`/`limitAllChannels` rules): at least one channel must be selected when the limit is on,
 * and the "same for all" cap must be a positive integer not exceeding the offer quantity.
 */
export const validateChannelLimit = (
  channelLimit: AdmissionFormValue['distribution']['channelLimit'],
  channelQuantity: number | null,
  t: TFunction
): string | undefined => {
  if (!channelLimit?.enabled) return undefined;

  const closed = collectClosedChannelIds(channelLimit.channels);
  const selectableCount = collectChannelIds(channelLimit.channels).filter((id) => !closed.has(id)).length;
  if (selectableCount > 0 && channelLimit.selectedChannelIds.length === 0) {
    return t('offers:admissionTicketSalesChannelsNoneSelected');
  }
  if (channelLimit.quotaMode !== CHANNEL_QUOTA_ALL) return undefined;

  const value = channelLimit.limitAllChannels;
  if (value === '') return t('offers:admissionSalesChannelsQuotaErrorRequired');
  return (
    cannotBeNegative({ value, message: t('offers:admissionSalesChannelsQuotaErrorNegative') }) ??
    maxDecimals({ value, decimals: WHOLE_NUMBER, message: t('offers:admissionSalesChannelsQuotaErrorDecimals') }) ??
    min({ value, minValue: 1, message: t('offers:admissionSalesChannelsQuotaErrorMin', { number: 1 }) }) ??
    (channelQuantity == null
      ? undefined
      : max({
          value: Number(value),
          maxValue: channelQuantity,
          message: t('offers:admissionSalesChannelsQuotaErrorMax', { quantity: channelQuantity }),
        }))
  );
};

export const valuesEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (moment.isMoment(a) || moment.isMoment(b)) {
    return moment.isMoment(a) && moment.isMoment(b) && a.isSame(b);
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, i) => valuesEqual(item, b[i]));
  }
  if (typeof a === 'object' && a !== null && typeof b === 'object' && b !== null) {
    const aRec = a as Record<string, unknown>;
    const bRec = b as Record<string, unknown>;
    const keys = Object.keys(aRec);
    return keys.length === Object.keys(bRec).length && keys.every((key) => valuesEqual(aRec[key], bRec[key]));
  }
  return false;
};

const getAtPath = (root: unknown, path: string[]): unknown => {
  let node = root;
  for (const key of path) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = (node as Record<string, unknown>)[key];
  }
  return node;
};

type IsDirty = (...path: string[]) => boolean;

const gate = (error: string | undefined, dirty: boolean): string | undefined => (error && dirty ? error : undefined);

const dropEmptyErrors = <T extends object>(errors: T): T => {
  const result = {} as T;
  for (const key of Object.keys(errors) as (keyof T)[]) {
    if (errors[key] !== undefined) result[key] = errors[key];
  }
  return result;
};

const selectVisibleBasicInfo = (e: BasicInfoErrors, isDirty: IsDirty): BasicInfoErrors => {
  const itemsDirty = isDirty('basicInfo', 'itemsPerTransaction') || isDirty('basicInfo', 'quantity');
  const salesDirty = isDirty('basicInfo', 'salesWindow');
  const downpaymentDirty = isDirty('basicInfo', 'downpayment') || isDirty('basicInfo', 'price');
  // The SIAE guest cap surfaces here but reacts to extra-guests and SIAE exclusion too.
  const guestsDirty =
    isDirty('basicInfo', 'guestsPerAdmission') || isDirty('basicInfo', 'extraGuests') || isDirty('regulation');
  return dropEmptyErrors({
    price: gate(e.price, isDirty('basicInfo', 'price')),
    quantity: gate(e.quantity, isDirty('basicInfo', 'quantity')),
    ddp: gate(e.ddp, isDirty('basicInfo', 'ddp')),
    downpayment: gate(e.downpayment, downpaymentDirty),
    guestsPerAdmission: gate(e.guestsPerAdmission, guestsDirty),
    extraGuestsMaxQty: e.extraGuestsMaxQty,
    extraGuestsPricePerGuest: e.extraGuestsPricePerGuest,
    itemsPerTransactionMin: gate(e.itemsPerTransactionMin, itemsDirty),
    itemsPerTransactionMax: gate(e.itemsPerTransactionMax, itemsDirty),
    salesStart: gate(e.salesStart, salesDirty),
    salesEnd: gate(e.salesEnd, salesDirty),
    passValidityStart: gate(e.passValidityStart, isDirty('basicInfo', 'passValidity', 'start')),
    passValidityEnd: gate(e.passValidityEnd, isDirty('basicInfo', 'passValidity', 'end')),
  });
};

const selectVisibleAdvanced = (e: AdvancedSettingsErrors, isDirty: IsDirty): AdvancedSettingsErrors =>
  dropEmptyErrors({
    drinksQuantity: gate(e.drinksQuantity, isDirty('advanced', 'drinks')),
    lowStockThresholdQuantity: gate(e.lowStockThresholdQuantity, isDirty('advanced', 'lowStockThreshold')),
    entranceTimeSlotRange: gate(e.entranceTimeSlotRange, isDirty('advanced', 'entranceTimeSlot')),
  });

export const selectVisibleErrors = (
  errors: AdmissionFormErrors,
  value: AdmissionFormValue,
  defaultValue: AdmissionFormValue
): AdmissionFormErrors => {
  const isDirty: IsDirty = (...path) => !valuesEqual(getAtPath(value, path), getAtPath(defaultValue, path));
  const visible: AdmissionFormErrors = {};

  if (errors.basicInfo) {
    const basicInfo = selectVisibleBasicInfo(errors.basicInfo, isDirty);
    if (hasAnyError(basicInfo)) visible.basicInfo = basicInfo;
  }
  if (errors.advanced) {
    const advanced = selectVisibleAdvanced(errors.advanced, isDirty);
    if (hasAnyError(advanced)) visible.advanced = advanced;
  }
  if (errors.addOns?.mandatoryNoSelection && isDirty('addOns')) visible.addOns = errors.addOns;
  if (errors.vatRate?.vatRate && isDirty('vatRate', 'vatRate')) visible.vatRate = errors.vatRate;
  if (errors.regulation && isDirty('regulation')) visible.regulation = errors.regulation;

  return visible;
};
